import { create } from 'zustand';
import type { IdeaListItem, IdeaStatus, Paginated } from '@shared/types';
import { api, errorMessage } from '../../lib/api';

/**
 * 看板数据。
 *
 * 沿用与无人机平台一致的 homegrown 模式（`loading / loaded / error` + 幂等 `load`），
 * 不引入请求缓存库：这里只有一个列表接口，加一层缓存反而多一处需要同步的状态。
 */

export interface IdeasFilters {
  statuses: IdeaStatus[];
  tags: string[];
  search: string;
}

interface IdeasState {
  items: IdeaListItem[];
  total: number;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  filters: IdeasFilters;
  load: (options?: { force?: boolean }) => Promise<void>;
  setFilters: (patch: Partial<IdeasFilters>) => void;
  /** 详情页改动后同步列表里的那一条，省掉一次全量刷新 */
  upsert: (item: IdeaListItem) => void;
  reset: () => void;
}

const DEFAULT_FILTERS: IdeasFilters = { statuses: [], tags: [], search: '' };

export const useIdeasStore = create<IdeasState>((set, get) => ({
  items: [],
  total: 0,
  loading: false,
  loaded: false,
  error: null,
  filters: DEFAULT_FILTERS,

  async load(options = {}) {
    if (get().loading) return;
    if (get().loaded && !options.force) return;

    set({ loading: true, error: null });
    const { statuses, tags, search } = get().filters;
    try {
      const result = await api.get<Paginated<IdeaListItem>>('/api/ideas', {
        query: {
          status: statuses.join(','),
          tag: tags.join(','),
          search,
          pageSize: 200,
        },
      });
      set({ items: result.items, total: result.total, loading: false, loaded: true, error: null });
    } catch (error) {
      set({ loading: false, error: errorMessage(error) });
    }
  },

  setFilters(patch) {
    set({ filters: { ...get().filters, ...patch } });
    void get().load({ force: true });
  },

  upsert(item) {
    const items = get().items;
    const index = items.findIndex((candidate) => candidate.id === item.id);
    if (index < 0) {
      set({ items: [item, ...items], total: get().total + 1 });
      return;
    }
    const next = [...items];
    next[index] = item;
    set({ items: next });
  },

  reset() {
    set({ items: [], total: 0, loading: false, loaded: false, error: null, filters: DEFAULT_FILTERS });
  },
}));
